// work-media-gallery v1: 作品媒体画廊组件（支持图片和视频）

import React, { useState, useEffect } from 'react';

import { getPortfolioImageUrl } from '../../../services/supabase/portfolio';
import { isValidVideoUrl } from '../../upload-portfolio/utils/formDataHelpers';

import ImageViewer from './ImageViewer';

/**
 * 作品媒体画廊组件（支持图片和视频）
 * @param {Array} images - 图片URL数组或图片路径数组
 * @param {string} videoUrl - 视频链接
 * @param {string} workTitle - 作品标题
 * @param {string} className - 额外的CSS类名
 */
const WorkMediaGallery = ({
  images = [],
  videoUrl = '',
  workTitle = 'Work',
  className = '',
}) => {
  // 调试日志
  console.log('[WorkMediaGallery] Props received:', {
    images,
    videoUrl,
    workTitle,
  });
  // 媒体查看器状态
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaItems, setMediaItems] = useState([]); // 包含图片和视频的混合数组

  // 获取图片URL
  useEffect(() => {
    const loadImageUrls = async () => {
      if (!images || images.length === 0) {
        setImageUrls([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const urls = [];

        console.log('[WorkMediaGallery] Processing images:', images);

        for (const image of images) {
          if (typeof image === 'string') {
            // 如果已经是完整的 URL，直接使用
            if (
              image.startsWith('data:image/') ||
              image.startsWith('blob:') ||
              image.startsWith('http')
            ) {
              if (image !== '/assets/placeholder.svg' && image.trim() !== '') {
                console.log(
                  '[WorkMediaGallery] Using direct URL:',
                  image.substring(0, 50) + '...'
                );
                urls.push(image);
              } else {
                console.log(
                  '[WorkMediaGallery] Skipping placeholder path:',
                  image
                );
              }
            } else {
              // 如果是文件路径，需要转换为 URL
              console.log('[WorkMediaGallery] Converting path to URL:', image);
              const result = await getPortfolioImageUrl(image);
              if (result.success && result.data?.url) {
                urls.push(result.data.url);
              } else {
                console.warn(
                  '[WorkMediaGallery] Failed to get URL for:',
                  image
                );
              }
            }
          }
        }

        console.log('[WorkMediaGallery] Final image URLs:', urls);
        setImageUrls(urls);
      } catch (error) {
        console.error('[WorkMediaGallery] Error loading images:', error);
        setImageUrls([]);
      } finally {
        setLoading(false);
      }
    };

    loadImageUrls();
  }, [images]);

  // 构建媒体项目数组（图片 + 视频）
  useEffect(() => {
    const items = [];

    // 添加图片
    imageUrls.forEach((url, index) => {
      items.push({
        type: 'image',
        url: url,
        index: index,
      });
    });

    // 如果有视频链接，添加到媒体数组中
    if (videoUrl && isValidVideoUrl(videoUrl)) {
      items.push({
        type: 'video',
        url: videoUrl,
        index: imageUrls.length,
      });
    }

    setMediaItems(items);
    console.log('[WorkMediaGallery] Media items:', items);
  }, [imageUrls, videoUrl]);

  // 处理媒体点击
  const handleMediaClick = index => {
    const item = mediaItems[index];
    if (item.type === 'image') {
      setCurrentMediaIndex(index);
      setIsImageViewerOpen(true);
    }
    // 视频点击不需要打开查看器，直接播放
  };

  // 处理上一张媒体
  const handlePreviousMedia = () => {
    if (currentMediaIndex > 0) {
      setCurrentMediaIndex(currentMediaIndex - 1);
    }
  };

  // 处理下一张媒体
  const handleNextMedia = () => {
    if (currentMediaIndex < mediaItems.length - 1) {
      setCurrentMediaIndex(currentMediaIndex + 1);
    }
  };

  // 关闭图片查看器
  const handleCloseImageViewer = () => {
    setIsImageViewerOpen(false);
  };

  // 获取视频嵌入URL
  const getVideoEmbedUrl = url => {
    if (!url) return '';

    // YouTube 处理
    if (url.includes('youtube.com/watch') || url.includes('youtu.be/')) {
      const videoId = url.includes('youtu.be/')
        ? url.split('youtu.be/')[1].split('?')[0]
        : url.split('v=')[1].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Vimeo 处理
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1].split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }

    return url;
  };

  // 如果正在加载，显示加载状态
  if (loading) {
    return (
      <div className={`${className}`}>
        <div className='relative aspect-square sm:aspect-video bg-gray-200 rounded-lg shadow-sm overflow-hidden flex items-center justify-center'>
          <div className='text-center text-gray-500'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500 mx-auto mb-2'></div>
            <p className='text-sm'>Loading media...</p>
          </div>
        </div>
      </div>
    );
  }

  // 如果没有媒体内容，显示空状态
  if (mediaItems.length === 0) {
    return (
      <div className={`${className}`}>
        <div className='relative aspect-square sm:aspect-video bg-gray-200 rounded-lg shadow-sm overflow-hidden flex items-center justify-center'>
          <div className='text-center text-gray-500'>
            <svg
              className='w-12 h-12 mx-auto mb-2'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1}
                d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
              />
            </svg>
            <p className='text-sm'>No media available</p>
          </div>
        </div>
      </div>
    );
  }

  const currentItem = mediaItems[currentMediaIndex];

  return (
    <div className={`${className}`}>
      {/* 媒体展示区域 */}
      <div className='relative'>
        <div className='relative aspect-square sm:aspect-video bg-white rounded-lg shadow-sm overflow-hidden'>
          {currentItem.type === 'image' ? (
            <img
              src={currentItem.url}
              alt={`${workTitle} - ${currentItem.index + 1}`}
              className='w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity duration-200'
              onClick={() => handleMediaClick(currentMediaIndex)}
            />
          ) : (
            <iframe
              src={getVideoEmbedUrl(currentItem.url)}
              title={`${workTitle} - Video`}
              className='w-full h-full'
              frameBorder='0'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
              allowFullScreen
            />
          )}

          {/* 媒体类型指示器 */}
          {currentItem.type === 'video' && (
            <div className='absolute top-4 right-4 bg-red-500 text-white px-2 py-1 rounded text-sm flex items-center gap-1'>
              <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                <path d='M8 5v14l11-7z' />
              </svg>
              Video
            </div>
          )}

          {/* 媒体序号指示器 */}
          {mediaItems.length > 1 && (
            <div className='absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded text-sm'>
              {currentMediaIndex + 1} / {mediaItems.length}
            </div>
          )}

          {/* 左导航箭头 */}
          {mediaItems.length > 1 && currentMediaIndex > 0 && (
            <button
              onClick={handlePreviousMedia}
              className='absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white px-2 py-1 sm:px-3 sm:py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 rounded text-sm sm:text-base md:text-lg lg:text-2xl xl:text-3xl hover:bg-black/70 transition-colors duration-200'
              aria-label='Previous media'
            >
              &lt;
            </button>
          )}

          {/* 右导航箭头 */}
          {mediaItems.length > 1 &&
            currentMediaIndex < mediaItems.length - 1 && (
              <button
                onClick={handleNextMedia}
                className='absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white px-2 py-1 sm:px-3 sm:py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 rounded text-sm sm:text-base md:text-lg lg:text-2xl xl:text-3xl hover:bg-black/70 transition-colors duration-200'
                aria-label='Next media'
              >
                &gt;
              </button>
            )}
        </div>
      </div>

      {/* 图片查看器模态框 */}
      {isImageViewerOpen && currentItem.type === 'image' && (
        <ImageViewer
          imageUrl={currentItem.url}
          imageAlt={`${workTitle} - ${currentItem.index + 1}`}
          isOpen={isImageViewerOpen}
          onClose={handleCloseImageViewer}
        />
      )}
    </div>
  );
};

export default WorkMediaGallery;
